import { FC, SVGProps, useMemo } from 'react'
import { EChartOption } from 'echarts'
import { EChart } from 'src/charts/EChart'
import { ReactComponent as BookingIcon } from 'src/assets/svg/booking-brand.svg'
import { ReactComponent as ExpediaIcon } from 'src/assets/svg/expedia-brand.svg'
import { ReactComponent as FacebookIcon } from 'src/assets/svg/facebook.svg'
import { ReactComponent as GlobeIcon } from 'src/assets/svg/Globe.svg'
import { ReactComponent as InstagramIcon } from 'src/assets/svg/instagram.svg'
import { ReactComponent as WhatsappIcon } from 'src/assets/svg/whatsapp.svg'
import { TruncatedTooltip } from 'src/components/TruncatedTooltip'
import styles from './sourcesSplit.module.scss'

const DONUT_SIZE = 84

const SOURCE_ICONS: Record<string, FC<SVGProps<SVGSVGElement>>> = {
  booking: BookingIcon,
  expedia: ExpediaIcon,
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  whatsapp: WhatsappIcon,
}

// Donut / dot colour per platform (mirrors the home overview's Total Sources).
const PLATFORM_COLORS: Record<string, string> = {
  booking: '#0A4FA3',
  expedia: '#F5D328',
  facebook: '#5368B9',
  instagram: '#EF5451',
  whatsapp: '#6CAB82',
}
const FALLBACK_COLOR = '#8080FF'

// booking / expedia brand svgs render black & white here.
const MONO_BRAND_SOURCES = new Set(['booking', 'expedia'])

interface SourcesSplitProps {
  sources: {
    chats: number
    id: string
    name: string
  }[]
}

const colorOf = (id: string) =>
  PLATFORM_COLORS[id.toLowerCase()] ?? FALLBACK_COLOR

// Platform legend + a chats-per-platform donut — the body of Total Sources.
export const SourcesSplit: FC<SourcesSplitProps> = ({ sources }) => {
  const totalChats = sources.reduce((sum, source) => sum + source.chats, 0)

  const donutOptions: EChartOption<EChartOption.SeriesPie> = useMemo(
    () => ({
      color: sources.map((source) => colorOf(source.id)),
      series: [
        {
          data: sources.map((source) => ({
            name: source.name,
            value: source.chats || 1,
          })),
          emphasis: { scale: false },
          itemStyle: { borderColor: '#F3F6FF', borderWidth: 1 },
          label: { show: false },
          labelLine: { show: false },
          radius: ['74%', '100%'],
          type: 'pie',
        } as EChartOption.SeriesPie,
      ],
      tooltip: { formatter: '{b}: {c}' },
    }),
    [sources]
  )

  return (
    <div className={styles.split}>
      <ul className={styles.legend}>
        {sources.map((source) => {
          const Icon = SOURCE_ICONS[source.id.toLowerCase()] ?? GlobeIcon
          const isMono = MONO_BRAND_SOURCES.has(source.id.toLowerCase())
          return (
            <li className={styles.item} key={source.id}>
              <Icon
                className={`${styles.icon} ${isMono ? styles.monoIcon : ''}`}
              />
              <TruncatedTooltip className={styles.name} text={source.name} />
              <span
                className={styles.dot}
                style={{ backgroundColor: colorOf(source.id) }}
              />
            </li>
          )
        })}
      </ul>
      <div className={styles.chart}>
        <div className={styles.chartLabel}>
          <span>Chats</span>
          <strong>{totalChats.toLocaleString('en-US')}</strong>
        </div>
        <EChart height={DONUT_SIZE} options={donutOptions} width={DONUT_SIZE} />
      </div>
    </div>
  )
}
